/**
 * Validation Review Dashboard
 *
 * FERP CRITICAL: LLM Fix Assistant human review interface
 * AI-powered validation error fixing with mandatory human-in-the-loop approval
 *
 * Architecture:
 * - Repository pattern: All GraphQL through LLMFixRepository
 * - Data-driven: Fix recommendations from Claude API backend
 * - Human approval: No automatic fixes, explicit accept/reject required
 * - Re-validation: Mandatory Phase 12 validation after applying fixes
 * - Complete audit: All decisions tracked (who/when/why for HIPAA/SOX)
 *
 * Flow:
 * 1. Display validation errors (red badges)
 * 2. User clicks "Request AI Fix Recommendations"
 * 3. Backend runs Phase 12 validation + Claude API analysis
 * 4. Display fix recommendations with confidence scores
 * 5. User reviews each fix (reasoning, before/after, diff)
 * 6. User accepts/rejects individual fixes or applies all
 * 7. Backend applies fixes + re-validates template
 * 8. Display re-validation results (pass/fail + remaining errors)
 *
 * Safety:
 * - Life-critical system: Patient/financial data integrity
 * - Confidence scoring: Visual indicator of AI reliability
 * - Diff display: Complete transparency of changes
 * - Audit trail: Immutable history in DynamoDB
 *
 * Usage:
 * ```tsx
 * <ValidationReviewDashboard
 *   templateId="healthcare-rcm-123"
 *   validationErrors={[
 *     { field_id: 'patient_dob', message: 'Invalid component: DateInput' }
 *   ]}
 *   onFixesApplied={() => router.push('/templates')}
 * />
 * ```
 */

import React, { useState } from 'react';
import {
  Container,
  Paper,
  Title,
  Text,
  Stack,
  Group,
  Button,
  Alert,
  Divider,
  Badge,
  Accordion,
  LoadingOverlay,
  Box,
  Tabs,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconSparkles,
  IconCheck,
  IconX,
  IconFileCode,
  IconClipboardCheck,
  IconExclamationCircle,
} from '@tabler/icons-react';
import { useValidationFixes } from '../hooks/useValidationFixes';
import { FixRecommendationCard } from './FixRecommendationCard';
import { TemplateDiffViewer } from './TemplateDiffViewer';

// ============================================================================
// COMPONENT PROPS
// ============================================================================

export interface ValidationError {
  field_id: string;
  step_id?: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface ValidationReviewDashboardProps {
  /** Template ID to analyze */
  templateId: string;

  /** Template name for display */
  templateName?: string;

  /** Current validation errors from Phase 12 */
  validationErrors: ValidationError[];

  /** Callback when fixes successfully applied */
  onFixesApplied?: (remainingErrors: number) => void;

  /** Callback when dashboard is closed */
  onClose?: () => void;

  /** Show compact view */
  compact?: boolean;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ValidationReviewDashboard: React.FC<ValidationReviewDashboardProps> = ({
  templateId,
  templateName = 'Template',
  validationErrors,
  onFixesApplied,
  onClose,
  compact = false,
}) => {
  const {
    recommendation,
    loading,
    error,
    applyResult,
    requestFixes,
    applyFixes,
    applyAllFixes,
    rejectFixes,
    clearResult,
  } = useValidationFixes(templateId);

  const [selectedFixIndices, setSelectedFixIndices] = useState<number[]>([]);
  const [activeTab, setActiveTab] = useState<string | null>('errors');

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleRequestFixes = async () => {
    await requestFixes(templateId);
    setActiveTab('recommendations'); // Switch to recommendations tab
  };

  const handleAcceptFix = (index: number) => {
    setSelectedFixIndices((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      }
      return [...prev, index];
    });
  };

  const handleRejectFix = (index: number) => {
    setSelectedFixIndices((prev) => prev.filter((i) => i !== index));
  };

  const handleApplySelected = async () => {
    if (!recommendation) return;
    if (selectedFixIndices.length === 0) {
      // If nothing selected, apply all fixes
      await applyAllFixes(recommendation.recommendation_id);
    } else {
      await applyFixes(recommendation.recommendation_id, selectedFixIndices);
    }

    // Callback with remaining error count
    if (applyResult && onFixesApplied) {
      onFixesApplied(applyResult.remaining_violations.length);
    }
  };

  const handleRejectAll = async () => {
    if (!recommendation) return;
    await rejectFixes(recommendation.recommendation_id, 'User rejected all recommendations');
    clearResult();
    setActiveTab('errors');
  };

  const handleStartOver = () => {
    clearResult();
    setSelectedFixIndices([]);
    setActiveTab('errors');
  };

  // ============================================================================
  // RENDER: LOADING STATE
  // ============================================================================

  if (loading && !recommendation) {
    return (
      <Container size="lg">
        <Paper shadow="sm" padding="xl" radius="md" withBorder pos="relative">
          <LoadingOverlay visible={true} />
          <Stack gap="md" ta="center" py={60}>
            <IconSparkles size={48} color="var(--mantine-color-blue-6)" />
            <Text size="lg" fw={600}>
              AI is analyzing validation errors...
            </Text>
            <Text size="sm" c="dimmed">
              This may take 10-30 seconds
            </Text>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // ============================================================================
  // RENDER: ERROR STATE
  // ============================================================================

  if (error) {
    return (
      <Container size="lg">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Failed to Generate Fix Recommendations"
          color="red"
        >
          {error}
          <Group mt="md" gap="sm">
            <Button variant="light" size="sm" onClick={handleRequestFixes}>
              Retry
            </Button>
            {onClose && (
              <Button variant="subtle" size="sm" onClick={onClose}>
                Close
              </Button>
            )}
          </Group>
        </Alert>
      </Container>
    );
  }

  // ============================================================================
  // RENDER: MAIN DASHBOARD
  // ============================================================================

  return (
    <Container size="xl">
      <Stack gap="lg">
        {/* Header */}
        <Paper shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between" wrap="nowrap">
            <Box>
              <Title order={2}>Validation Review Dashboard</Title>
              <Text size="sm" c="dimmed">
                {templateName}
              </Text>
            </Box>
            <Group gap="sm">
              {onClose && (
                <Button variant="subtle" onClick={onClose}>
                  Close
                </Button>
              )}
            </Group>
          </Group>
        </Paper>

        {/* Main content tabs */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab
              value="errors"
              leftSection={<IconExclamationCircle size={16} />}
              rightSection={
                <Badge size="sm" color="red" circle>
                  {validationErrors.length}
                </Badge>
              }
            >
              Validation Errors
            </Tabs.Tab>
            <Tabs.Tab
              value="recommendations"
              leftSection={<IconSparkles size={16} />}
              disabled={!recommendation}
              rightSection={
                recommendation && (
                  <Badge size="sm" color="blue" circle>
                    {recommendation.suggested_fixes.length}
                  </Badge>
                )
              }
            >
              AI Recommendations
            </Tabs.Tab>
            <Tabs.Tab
              value="diff"
              leftSection={<IconFileCode size={16} />}
              disabled={!recommendation}
            >
              Template Diff
            </Tabs.Tab>
            {applyResult && (
              <Tabs.Tab value="results" leftSection={<IconClipboardCheck size={16} />}>
                Apply Results
              </Tabs.Tab>
            )}
          </Tabs.List>

          {/* Tab 1: Validation Errors */}
          <Tabs.Panel value="errors" pt="lg">
            <Stack gap="md">
              <Alert
                icon={<IconAlertCircle size={16} />}
                title={`${validationErrors.length} Validation Error${validationErrors.length === 1 ? '' : 's'} Found`}
                color="red"
              >
                Runtime validation detected errors that would prevent this template from rendering correctly.
              </Alert>

              <Accordion variant="contained">
                {validationErrors.map((error, index) => (
                  <Accordion.Item key={index} value={`error-${index}`}>
                    <Accordion.Control>
                      <Group gap="xs">
                        <Badge color="red" size="sm">
                          {error.severity.toUpperCase()}
                        </Badge>
                        <Text size="sm">
                          Field: {error.field_id}
                          {error.step_id && ` (Step: ${error.step_id})`}
                        </Text>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Text size="sm">{error.message}</Text>
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>

              {!recommendation && (
                <Button
                  size="lg"
                  leftSection={<IconSparkles size={20} />}
                  onClick={handleRequestFixes}
                  loading={loading}
                  fullWidth
                >
                  Request AI Fix Recommendations
                </Button>
              )}
            </Stack>
          </Tabs.Panel>

          {/* Tab 2: AI Recommendations */}
          <Tabs.Panel value="recommendations" pt="lg">
            {recommendation && (
              <Stack gap="lg">
                {/* Overall confidence */}
                <Alert
                  icon={<IconSparkles size={16} />}
                  title={`AI Confidence: ${Math.round(recommendation.confidence * 100)}%`}
                  color={recommendation.confidence > 0.9 ? 'green' : recommendation.confidence > 0.7 ? 'yellow' : 'red'}
                >
                  <Text size="sm">{recommendation.reasoning}</Text>
                </Alert>

                {/* Fix cards */}
                <Stack gap="md">
                  {recommendation.suggested_fixes.map((fix, index) => (
                    <FixRecommendationCard
                      key={index}
                      fix={fix}
                      index={index}
                      onAccept={handleAcceptFix}
                      onReject={handleRejectFix}
                      disabled={loading}
                      compact={compact}
                    />
                  ))}
                </Stack>

                <Divider />

                {/* Actions */}
                <Group justify="space-between">
                  <Button variant="subtle" color="red" leftSection={<IconX size={16} />} onClick={handleRejectAll} disabled={loading}>
                    Reject All
                  </Button>
                  <Button
                    variant="filled"
                    color="green"
                    leftSection={<IconCheck size={16} />}
                    onClick={handleApplySelected}
                    disabled={loading}
                    loading={loading}
                  >
                    {selectedFixIndices.length === 0
                      ? `Apply All ${recommendation.suggested_fixes.length} Fixes`
                      : `Apply ${selectedFixIndices.length} Selected Fix${selectedFixIndices.length === 1 ? '' : 'es'}`}
                  </Button>
                </Group>
              </Stack>
            )}
          </Tabs.Panel>

          {/* Tab 3: Template Diff */}
          <Tabs.Panel value="diff" pt="lg">
            {recommendation && <TemplateDiffViewer diff={recommendation.diff} templateName={templateName} maxHeight={600} />}
          </Tabs.Panel>

          {/* Tab 4: Apply Results */}
          {applyResult && (
            <Tabs.Panel value="results" pt="lg">
              <Stack gap="md">
                {applyResult.validation_passed ? (
                  <Alert icon={<IconCheck size={16} />} title="Template Validation Passed!" color="green">
                    All {applyResult.fixes_applied} fixes were applied successfully. Template is now ready for use.
                  </Alert>
                ) : (
                  <Alert icon={<IconAlertCircle size={16} />} title="Validation Still Failing" color="yellow">
                    Applied {applyResult.fixes_applied} fixes, but {applyResult.remaining_violations.length} errors remain. Additional fixes may be needed.
                  </Alert>
                )}

                {applyResult.remaining_violations.length > 0 && (
                  <Accordion variant="contained">
                    {applyResult.remaining_violations.map((violation, index) => (
                      <Accordion.Item key={index} value={`violation-${index}`}>
                        <Accordion.Control>
                          <Badge color="red" size="sm">
                            REMAINING ERROR
                          </Badge>
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Text size="sm">{violation}</Text>
                        </Accordion.Panel>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                )}

                <Divider />

                <Group justify="space-between">
                  <Button variant="subtle" onClick={handleStartOver}>
                    Start Over
                  </Button>
                  {applyResult.validation_passed && onFixesApplied && (
                    <Button color="green" onClick={() => onFixesApplied(0)}>
                      Continue
                    </Button>
                  )}
                </Group>
              </Stack>
            </Tabs.Panel>
          )}
        </Tabs>
      </Stack>
    </Container>
  );
};

export default ValidationReviewDashboard;
